import type { FunctionComponent, ReactNode } from 'react';

import { type Theme, useTheme } from '@dr.pogodin/react-utils';

import defaultTheme from 'themes/GlassCard/default.scss';

export type ThemeT = Theme<
  | 'container'
  | 'content'
  | 'effect'
  | 'effectBase'
  | 'effectShine'
  | 'effectTint'
>;

type PropsT = {
  children: ReactNode;
  theme?: ThemeT;
};

/**
 * Implements a liquid card component.
 */
const GlassCard: FunctionComponent<PropsT> = ({ children, theme }) => {
  const composed = useTheme('GlassCard', defaultTheme, theme);

  return (
    <div className={composed.container}>
      {/*
        NOTE: We can't move it to a stand-alone SVG easily, as our setup for
        SVG imports passes all imported files through an optimizer, which would
        remove <filter> from this component as unused.
        TODO: Find a way to opt-out of the optimizitaion for a specific import?
        Ticket for this in a Babel preset responsible for SVG imports processing:
        https://github.com/birdofpreyru/babel-preset-svgr/issues/26
      */}
      <svg styleName="filterSvg">
        <filter
          filterUnits="objectBoundingBox"
          height="100%"

          // BEWARE: It is a random UUID, but it MUST MATCH the value hard-coded
          // into SCSS theme.
          id="c863bbb9-41b1-421d-8a8d-b2b73c86d204"

          width="100%"
          x="0%"
          y="0%"
        >
          <feTurbulence
            baseFrequency="0.01 0.01"
            numOctaves="1"
            result="turbulence"
            seed="5"
            type="fractalNoise"
          />

          <feComponentTransfer in="turbulence" result="mapped">
            <feFuncR amplitude="1" exponent="10" offset="0.5" type="gamma" />
            <feFuncG amplitude="0" exponent="1" offset="0" type="gamma" />
            <feFuncB amplitude="0" exponent="1" offset="0.5" type="gamma" />
          </feComponentTransfer>

          <feGaussianBlur in="turbulence" result="softMap" stdDeviation="3" />

          <feSpecularLighting
            in="softMap"
            lightingColor="white"
            result="specLight"
            specularConstant="1"
            specularExponent="100"
            surfaceScale="5"
          >
            <fePointLight x="-200" y="-200" z="300" />
          </feSpecularLighting>

          <feComposite
            in="specLight"
            k1="0"
            k2="1"
            k3="1"
            k4="0"
            operator="arithmetic"
            result="litImage"
          />

          <feDisplacementMap
            in="SourceGraphic"
            in2="softMap"
            scale="150"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </svg>
      <div className={`${composed.effect} ${composed.effectBase}`} />
      <div className={`${composed.effect} ${composed.effectTint}`} />
      <div className={`${composed.effect} ${composed.effectShine}`} />
      <div className={composed.content}>{children}</div>
    </div>
  );
};

export default GlassCard;
